import clsx from 'clsx'
import { useCallback, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import $type from '@tandem/ui/type.css'
import $form from '@tandem/ui/form.css'
import Spinner from '@tandem/ui/Spinner'
import Modal from '@tandem/ui/Modal'
import $modal from '@tandem/ui/Modal/styles.css'
import $table from '@tandem/ui/table.css'

import { useTRPC, type RouterOutputs } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { UserPlusIcon, PencilIcon as Edit2Icon, OctagonXIcon as XOctagonIcon } from 'lucide-react'
import { StoreForm } from '@/stores/StoreForm'
import CreateStoreForm from '@/stores/CreateStoreForm'
import $ from './-styles.css'

export const Route = createFileRoute('/stores/')({ component: StoresPage })

function StoresPage() {
  let [showModal, setShowModal] = useState(false)
  let [activeStore, setActiveStore] = useState<RouterOutputs['store']['list'][number] | null>(null)

  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let { company } = useAuth()

  let { data: stores = [], isFetching: isFetchingStores } = useQuery(
    trpc.store.list.queryOptions({ company_id: company!.company_id }),
  )

  let deletion = useMutation(
    trpc.store.delete.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(trpc.store.list.queryFilter())
      },
    }),
  )

  let closeModal = useCallback(() => {
    setShowModal(false)
    setActiveStore(null)
  }, [])

  return (
    <div class={$.container}>
      <header class={$.header}>
        <div class={$.search}>
          <input type='text' placeholder='Найти' />
        </div>
        <div class={$.controls}>
          <button
            class={$.create}
            onClick={() => {
              setActiveStore(null)
              setShowModal(true)
            }}
          >
            <UserPlusIcon />
            Добавить торговую точку
          </button>
        </div>
      </header>

      {stores.length > 0 ? (
        <table class={clsx('unstyled', $table.table)}>
          <thead>
            <tr>
              <th>Название</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr key={store.id}>
                <td class={$.storeName}>{store.name}</td>
                <td>
                  <div class={$.actions}>
                    <button
                      class='link'
                      onClick={() => {
                        setActiveStore(store)
                        setShowModal(true)
                      }}
                    >
                      <Edit2Icon />
                      Редактировать
                    </button>
                    <button
                      class='link danger'
                      disabled={deletion.isPending}
                      onClick={() => {
                        if (confirm(`Удалить торговую точку «${store.name}»?`)) {
                          deletion.mutate({ id: store.id })
                        }
                      }}
                    >
                      {deletion.isPending ? <Spinner /> : <XOctagonIcon />}
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p class={$type.muted}>Начни с добавления торговой точки.</p>
      )}

      {isFetchingStores && <Spinner />}

      <Modal open={showModal} onClose={closeModal}>
        <div class={$modal.header}>
          <h2>{activeStore ? 'Редактировать торговую точку' : 'Добавить торговую точку'}</h2>
        </div>
        {activeStore ? (
          <StoreForm
            store={activeStore}
            onCancel={closeModal}
            onSuccess={closeModal}
            class={$form.modal}
          />
        ) : (
          <CreateStoreForm
            companyId={company!.company_id}
            onCancel={closeModal}
            onSuccess={closeModal}
            class={$form.modal}
          />
        )}
      </Modal>
    </div>
  )
}
